"use client";

import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import { useResponsive } from "@/hooks/useResponsive";
import SectionTitle from "@/components/SectionTitle";

interface InteriorGroup {
  id: string;
  title: string;
  images: string[];
}

interface InteriorSectionProps {
  groups: InteriorGroup[];
  onGroupClick: (index: number) => void;
  title: string;
  subTitle: string;
  des: string;
}

export default function InteriorSection({
  title,
  subTitle,
  des,
  groups,
  onGroupClick,
}: InteriorSectionProps) {
  const { isMobile, isMobileLg } = useResponsive();
  const showMobileSlider = isMobile || isMobileLg;

  if (!groups || groups.length === 0) return null;

  const aspectClasses = [
    "aspect-[3/4] md:h-[420px]", // Studio
    "aspect-[4/3] md:h-[195px]", // 1PN
    "aspect-[4/3] md:h-[195px]", // 2PN
    "aspect-[4/3] md:h-[195px]", // 3PN
    "aspect-[3/4] md:h-[300px]", // Workspace
    "aspect-[3/4] md:h-[300px]", // Villa
  ];

  return (
    <section
      id="gallery"
      className="relative w-full bg-transparent py-12 md:py-16 border-t border-black/5 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {showMobileSlider ? (
          <div className="space-y-6">
            {/* Mobile Title */}
            <SectionTitle
              title={title}
              subtitle={subTitle}
              theme="green"
              subtitleColor="primaryHover"
              underlineColor="primaryHover"
            />

            {/* Mobile Description */}
            <p className="text-xs text-black/60 font-semibold leading-relaxed text-center max-w-xl mx-auto">
              {des}
            </p>

            {/* Mobile Horizontal Slider */}
            <div className="flex overflow-x-auto pb-4 gap-6 scrollbar-none snap-x snap-mandatory w-full scroll-smooth">
              {groups.map((group, idx) => (
                <div
                  key={group.id}
                  onClick={() => onGroupClick?.(idx)}
                  className="w-[280px] shrink-0 snap-start relative aspect-[3/4] rounded-2xl overflow-hidden shadow-md border border-black/5 group cursor-pointer"
                >
                  <SmoothImage
                    src={group.images[0] || "/images/hero_skyline.png"}
                    alt={group.title}
                    fill
                    sizes="280px"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-all duration-300" />
                  <div className="absolute top-4 left-4 text-[10px] text-white/70 font-bold uppercase tracking-wider">
                    {`${group.images.length} Thiết kế`}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="text-sm font-extrabold text-white uppercase tracking-wide mb-1">
                      {group.title}
                    </h4>
                    <span className="text-[10px] font-black text-third uppercase tracking-wider flex items-center gap-1 group-hover:opacity-85">
                      Chi tiết nội thất
                      <span>→</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {/* COLUMN 1: Text Intro + Studio Card */}
            <div className="flex flex-col justify-between gap-6">
              <div className="space-y-4 pr-2">
                <SectionTitle
                  title={title}
                  subtitle={subTitle}
                  align="left"
                  theme="green"
                  subtitleColor="primaryHover"
                  underlineColor="primaryHover"
                  className="hidden md:block"
                />
                <p className="text-xs text-black/60 font-semibold leading-relaxed text-justify md:text-left">
                  {des}
                </p>
              </div>

              {/* Studio Card (Tall) */}
              {groups[0] && (
                <div
                  onClick={() => onGroupClick?.(0)}
                  className={`relative ${aspectClasses[0] || "aspect-[3/4] md:h-[420px]"} w-full rounded-2xl overflow-hidden shadow-md border border-black/5 group cursor-pointer`}
                >
                  <SmoothImage
                    src={groups[0].images[0] || "/images/hero_skyline.png"}
                    alt={groups[0].title}
                    fill
                    sizes="(min-width: 1024px) 25vw, 90vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-all duration-300" />

                  <div className="absolute top-4 left-4 text-[10px] text-white/70 font-bold uppercase tracking-wider">
                    {`${groups[0].images.length} Thiết kế`}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="text-sm font-extrabold text-white uppercase tracking-wide mb-1">
                      {groups[0].title}
                    </h4>
                    <span className="text-[10px] font-black text-third uppercase tracking-wider flex items-center gap-1 group-hover:opacity-85">
                      Chi tiết nội thất
                      <span>→</span>
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* COLUMN 2: Up to 3 Square Cards Stacked */}
            {groups.slice(1, 4).length > 0 && (
              <div className="flex flex-col gap-6 justify-between">
                {[1, 2, 3].map((idx) => {
                  const group = groups[idx];
                  if (!group) return null;
                  return (
                    <div
                      key={group.id}
                      onClick={() => onGroupClick?.(idx)}
                      className={`relative ${aspectClasses[idx] || "aspect-[4/3] md:h-[195px]"} w-full rounded-2xl overflow-hidden shadow-md border border-black/5 group cursor-pointer`}
                    >
                      <SmoothImage
                        src={group.images[0] || "/images/hero_skyline.png"}
                        alt={group.title}
                        fill
                        sizes="(min-width: 1024px) 25vw, 90vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-all duration-300" />

                      <div className="absolute top-4 left-4 text-[10px] text-white/70 font-bold uppercase tracking-wider">
                        {`${group.images.length} Thiết kế`}
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h4 className="text-sm font-extrabold text-white uppercase tracking-wide mb-1">
                          {group.title}
                        </h4>
                        <span className="text-[10px] font-black text-third uppercase tracking-wider flex items-center gap-1 group-hover:opacity-85">
                          Chi tiết nội thất
                          <span>→</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* COLUMN 3: Portrait Cards Stacked */}
            {groups.slice(4).length > 0 && (
              <div className="flex flex-col gap-6 justify-between">
                {groups.slice(4).map((group, relativeIdx) => {
                  const idx = relativeIdx + 4;
                  return (
                    <div
                      key={group.id}
                      onClick={() => onGroupClick?.(idx)}
                      className={`relative ${aspectClasses[idx] || "aspect-[3/4] md:h-[300px]"} w-full rounded-2xl overflow-hidden shadow-md border border-black/5 group cursor-pointer`}
                    >
                      <SmoothImage
                        src={group.images[0] || "/images/hero_skyline.png"}
                        alt={group.title}
                        fill
                        sizes="(min-width: 1024px) 25vw, 90vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-all duration-300" />

                      <div className="absolute top-4 left-4 text-[10px] text-white/70 font-bold uppercase tracking-wider">
                        {`${group.images.length} Thiết kế`}
                      </div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h4 className="text-sm font-extrabold text-white uppercase tracking-wide mb-1">
                          {group.title}
                        </h4>
                        <span className="text-[10px] font-black text-third uppercase tracking-wider flex items-center gap-1 group-hover:opacity-85">
                          Chi tiết nội thất
                          <span>→</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
